import React, { useState, useEffect } from 'react';
import {
  LayoutGrid, Plus, Trash2, Edit, Save, RotateCcw, Settings, Type, FileText, Hash, Mail, Link, 
  Calendar, Clock, ChevronDown, CheckSquare, Check, Circle, Upload, TestTube, BarChart2, 
  Eye, EyeOff, Search, Filter, Copy, ArrowUp, ArrowDown, AlertCircle, CheckCircle, Monitor
} from 'lucide-react';
import MockDataService from '../../services/MockDataService';

const CustomFieldsManagement = () => {
  const [customFields, setCustomFields] = useState([]);
  const [fieldTypes, setFieldTypes] = useState([]);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newField, setNewField] = useState({
    name: '',
    label: '',
    type: 'text',
    description: '',
    required: false,
    options: [],
    validation: {
      required: false,
      minLength: null,
      maxLength: null,
      pattern: null,
      min: null,
      max: null
    },
    display: {
      position: 'top',
      width: 'half',
      showInList: true,
      showInSearch: true
    },
    dependencies: [],
    ticketTypes: [],
    departments: []
  });
  const [editingField, setEditingField] = useState(null);
  const [activeTab, setActiveTab] = useState('fields'); // 'fields', 'preview', 'testing', 'analytics'
  const [previewFormData, setPreviewFormData] = useState({});
  const [testData, setTestData] = useState({
    fieldId: '',
    testValue: '',
    testType: 'validation'
  });
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [fieldsResponse, typesResponse, ticketTypesResponse, departmentsResponse] = await Promise.all([
        MockDataService.getCustomFields(),
        MockDataService.getFieldTypes(),
        MockDataService.getTicketTypes(),
        MockDataService.getDepartmentsNew()
      ]);

      if (fieldsResponse.success) {
        setCustomFields(fieldsResponse.data || []);
        if (fieldsResponse.data && fieldsResponse.data.length > 0) {
          setTestData(prev => ({ ...prev, fieldId: fieldsResponse.data[0].id }));
        }
      } else {
        setCustomFields([]);
        setError(fieldsResponse.message || 'Failed to load custom fields');
      }
      
      if (typesResponse.success) {
        // Ensure we always set an array, handle if data is an object with types property
        const typesData = typesResponse.data;
        if (Array.isArray(typesData)) {
          setFieldTypes(typesData);
        } else if (typesData && Array.isArray(typesData.types)) {
          setFieldTypes(typesData.types);
        } else {
          setFieldTypes([]);
        }
      } else {
        setFieldTypes([]);
      }
      
      if (ticketTypesResponse.success) {
        setTicketTypes(ticketTypesResponse.data || []);
      } else {
        setTicketTypes([]);
      }
      
      if (departmentsResponse.success) {
        setDepartments(departmentsResponse.data || []);
      } else {
        setDepartments([]);
      }
    } catch (err) {
      setError('Failed to fetch custom fields data.');
      setCustomFields([]);
      setFieldTypes([]);
      setTicketTypes([]);
      setDepartments([]);
      console.error('Error fetching custom fields data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateField = async () => {
    try {
      const response = await MockDataService.createCustomField(newField);
      if (response.success) {
        setCustomFields([...customFields, response.data]);
        setShowCreateForm(false);
        setNewField({
          name: '',
          label: '',
          type: 'text',
          description: '',
          required: false,
          options: [],
          validation: {
            required: false,
            minLength: null,
            maxLength: null,
            pattern: null,
            min: null,
            max: null
          },
          display: {
            position: 'top',
            width: 'half',
            showInList: true,
            showInSearch: true
          },
          dependencies: [],
          ticketTypes: [],
          departments: []
        });
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError('Failed to create custom field.');
      console.error('Error creating custom field:', err);
    }
  };

  const handleUpdateField = async (fieldId, updatedData) => {
    try {
      const response = await MockDataService.updateCustomField(fieldId, updatedData);
      if (response.success) {
        setCustomFields(customFields.map(f => f.id === fieldId ? response.data : f));
        setEditingField(null);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError('Failed to update custom field.');
      console.error('Error updating custom field:', err);
    }
  };

  const handleDeleteField = async (fieldId) => {
    try {
      const response = await MockDataService.deleteCustomField(fieldId);
      if (response.success) {
        setCustomFields(customFields.filter(f => f.id !== fieldId));
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError('Failed to delete custom field.');
      console.error('Error deleting custom field:', err);
    }
  };

  const handleTestField = async () => {
    try {
      const response = await MockDataService.testCustomField(testData.fieldId, {
        value: testData.testValue,
        type: testData.testType
      });
      if (response.success) {
        setTestResult(response.data);
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError('Failed to test custom field.');
      console.error('Error testing custom field:', err);
    }
  };

  const getFieldTypeIcon = (type) => {
    const icons = {
      text: Type,
      textarea: FileText,
      number: Hash,
      email: Mail,
      url: Link,
      date: Calendar,
      datetime: Clock,
      select: ChevronDown,
      multiselect: CheckSquare,
      checkbox: Check,
      radio: Circle,
      file: Upload
    };
    return icons[type] || Type;
  };

  const getFieldTypeColor = (type) => {
    const colors = {
      text: '#3b82f6',
      textarea: '#8b5cf6',
      number: '#10b981',
      email: '#f59e0b',
      url: '#06b6d4',
      date: '#ef4444',
      datetime: '#f97316',
      select: '#84cc16',
      multiselect: '#ec4899',
      checkbox: '#6366f1',
      radio: '#14b8a6',
      file: '#64748b'
    };
    return colors[type] || '#3b82f6';
  };

  const getPositionColor = (position) => {
    const colors = {
      top: '#10b981',
      middle: '#f59e0b',
      bottom: '#ef4444'
    };
    return colors[position] || '#6b7280';
  };

  const getWidthColor = (width) => {
    const colors = {
      quarter: '#3b82f6',
      half: '#8b5cf6',
      full: '#10b981'
    };
    return colors[width] || '#6b7280';
  };

  // Render preview field based on type
  const renderPreviewField = (field) => {
    const value = previewFormData[field.id] || '';
    const handleChange = (newValue) => {
      setPreviewFormData(prev => ({
        ...prev,
        [field.id]: newValue
      }));
    };

    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={field.placeholder || `Enter ${field.label?.toLowerCase() || 'value'}`}
            required={field.required}
            minLength={field.validation?.minLength}
            maxLength={field.validation?.maxLength}
            pattern={field.validation?.pattern}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        );

      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={field.placeholder || `Enter ${field.label?.toLowerCase() || 'value'}`}
            required={field.required}
            minLength={field.validation?.minLength}
            maxLength={field.validation?.maxLength}
            rows={field.rows || 3}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={field.placeholder || `Enter ${field.label?.toLowerCase() || 'number'}`}
            required={field.required}
            min={field.validation?.min}
            max={field.validation?.max}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        );

      case 'email':
        return (
          <input
            type="email"
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={field.placeholder || `Enter email address`}
            required={field.required}
            pattern={field.validation?.pattern}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        );

      case 'url':
        return (
          <input
            type="url"
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={field.placeholder || `Enter URL`}
            required={field.required}
            pattern={field.validation?.pattern}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        );

      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            required={field.required}
            min={field.validation?.min}
            max={field.validation?.max}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        );

      case 'datetime':
        return (
          <input
            type="datetime-local"
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            required={field.required}
            min={field.validation?.min}
            max={field.validation?.max}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        );

      case 'time':
        return (
          <input
            type="time"
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            required={field.required}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        );

      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            required={field.required}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select {field.label?.toLowerCase() || 'option'}</option>
            {field.options && field.options.length > 0 ? (
              field.options.map((option, idx) => (
                <option key={idx} value={option.value || option.label}>
                  {option.label || option.value}
                </option>
              ))
            ) : (
              <option value="option1">Option 1</option>
            )}
          </select>
        );

      case 'multiselect':
        return (
          <select
            multiple
            value={Array.isArray(value) ? value : []}
            onChange={(e) => {
              const selected = Array.from(e.target.selectedOptions, option => option.value);
              handleChange(selected);
            }}
            required={field.required}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            size={Math.min((field.options?.length || 3) + 1, 5)}
          >
            {field.options && field.options.length > 0 ? (
              field.options.map((option, idx) => (
                <option key={idx} value={option.value || option.label}>
                  {option.label || option.value}
                </option>
              ))
            ) : (
              <>
                <option value="option1">Option 1</option>
                <option value="option2">Option 2</option>
                <option value="option3">Option 3</option>
              </>
            )}
          </select>
        );

      case 'checkbox':
        return (
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={value === true || value === 'true'}
              onChange={(e) => handleChange(e.target.checked)}
              required={field.required}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label className="text-sm text-gray-700 dark:text-gray-300">
              {field.checkboxLabel || 'Check this box'}
            </label>
          </div>
        );

      case 'radio':
        return (
          <div className="space-y-2">
            {field.options && field.options.length > 0 ? (
              field.options.map((option, idx) => (
                <div key={idx} className="flex items-center">
                  <input
                    type="radio"
                    name={`field_${field.id}`}
                    value={option.value || option.label}
                    checked={value === (option.value || option.label)}
                    onChange={(e) => handleChange(e.target.value)}
                    required={field.required}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <label className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    {option.label || option.value}
                  </label>
                </div>
              ))
            ) : (
              <>
                <div className="flex items-center">
                  <input
                    type="radio"
                    name={`field_${field.id}`}
                    value="option1"
                    checked={value === 'option1'}
                    onChange={(e) => handleChange(e.target.value)}
                    required={field.required}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <label className="ml-2 text-sm text-gray-700 dark:text-gray-300">Option 1</label>
                </div>
                <div className="flex items-center">
                  <input
                    type="radio"
                    name={`field_${field.id}`}
                    value="option2"
                    checked={value === 'option2'}
                    onChange={(e) => handleChange(e.target.value)}
                    required={field.required}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                  />
                  <label className="ml-2 text-sm text-gray-700 dark:text-gray-300">Option 2</label>
                </div>
              </>
            )}
          </div>
        );

      case 'file':
        return (
          <div>
            <input
              type="file"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  handleChange(file.name);
                }
              }}
              required={field.required}
              accept={field.accept || '*'}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {value && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Selected: {value}
              </p>
            )}
          </div>
        );

      case 'boolean':
        return (
          <select
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            required={field.required}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        );

      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleChange(e.target.value)}
            placeholder={`Enter ${field.label?.toLowerCase() || 'value'}`}
            required={field.required}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 bg-white dark:bg-gray-800 rounded-lg shadow-md">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 bg-white dark:bg-gray-800 rounded-lg shadow-md">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
        <LayoutGrid className="mr-3" size={28} />
        Custom Fields Management
      </h1>
      <p className="text-gray-700 dark:text-gray-300 mb-8">
        Create and manage custom fields for tickets to capture additional relevant information and improve data collection.
      </p>

      {error && (
        <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="mb-6">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'fields', label: 'Custom Fields', icon: LayoutGrid },
              { id: 'preview', label: 'Form Preview', icon: Monitor },
              { id: 'testing', label: 'Field Testing', icon: TestTube },
              { id: 'analytics', label: 'Analytics', icon: BarChart2 }
            ].map((tab) => {
              const IconComponent = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
                    activeTab === tab.id
                      ? 'border-[#006600] text-[#006600]'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  <IconComponent className="mr-2" size={16} />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Custom Fields Tab */}
      {activeTab === 'fields' && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Custom Fields ({customFields.length})
            </h2>
            <button
              onClick={() => setShowCreateForm(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center"
               style={{ background: 'linear-gradient(135deg,#006600 0%,#BB0000 100%)' }}
            >
              <Plus className="mr-2" size={16} />
              Add Field
            </button>
          </div>

          {/* Create Field Form */}
          {showCreateForm && (
            <div className="mb-6 p-6 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Create New Custom Field
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Field Name
                  </label>
                  <input
                    type="text"
                    value={newField.name}
                    onChange={(e) => setNewField({ ...newField, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                    placeholder="e.g., impact_level"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Field Label
                  </label>
                  <input
                    type="text"
                    value={newField.label}
                    onChange={(e) => setNewField({ ...newField, label: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                    placeholder="e.g., Business Impact"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Field Type
                  </label>
                  <select
                    value={newField.type}
                    onChange={(e) => setNewField({ ...newField, type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                  >
                    {Array.isArray(fieldTypes) && fieldTypes.length > 0 ? fieldTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    )) : (
                      <option value="">No field types available</option>
                    )}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Required Field
                  </label>
                  <input
                    type="checkbox"
                    checked={newField.required}
                    onChange={(e) => setNewField({ ...newField, required: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    value={newField.description}
                    onChange={(e) => setNewField({ ...newField, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                    rows="3"
                    placeholder="Describe the purpose of this field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Display Position
                  </label>
                  <select
                    value={newField.display.position}
                    onChange={(e) => setNewField({ 
                      ...newField, 
                      display: { ...newField.display, position: e.target.value }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                  >
                    <option value="top">Top</option>
                    <option value="middle">Middle</option>
                    <option value="bottom">Bottom</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Field Width
                  </label>
                  <select
                    value={newField.display.width}
                    onChange={(e) => setNewField({ 
                      ...newField, 
                      display: { ...newField.display, width: e.target.value }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                  >
                    <option value="quarter">Quarter Width</option>
                    <option value="half">Half Width</option>
                    <option value="full">Full Width</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-4">
                <button
                  onClick={() => setShowCreateForm(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateField}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center"
                >
                  <Save className="mr-2" size={16} />
                  Create Field
                </button>
              </div>
            </div>
          )}

          {/* Fields List */}
          <div className="space-y-4">
            {customFields && customFields.length > 0 ? customFields.map((field) => {
              const IconComponent = getFieldTypeIcon(field.type);
              const fieldColor = getFieldTypeColor(field.type);
              const positionColor = getPositionColor(field.display?.position);
              const widthColor = getWidthColor(field.display?.width);
              
              return (
                <div key={field.id} className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div 
                        className="w-12 h-12 rounded-lg flex items-center justify-center mr-4"
                        style={{ backgroundColor: fieldColor + '20' }}
                      >
                        <IconComponent 
                          size={24} 
                          style={{ color: fieldColor }}
                        />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {field.label}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400">
                          {field.name || 'Unnamed'} • {field.type || 'Unknown'} • {field.required ? 'Required' : 'Optional'}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {field.description}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setEditingField(field.id)}
                        className="p-2 text-gray-600 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteField(field.id)}
                        className="p-2 text-gray-600 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-gray-50 dark:bg-gray-600 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center">
                        <Settings className="mr-2" size={16} />
                        Display Settings
                      </h4>
                      <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                        <div className="flex items-center">
                          <div 
                            className="w-3 h-3 rounded-full mr-2"
                            style={{ backgroundColor: positionColor }}
                          ></div>
                          Position: {field.display?.position || 'Not set'}
                        </div>
                        <div className="flex items-center">
                          <div 
                            className="w-3 h-3 rounded-full mr-2"
                            style={{ backgroundColor: widthColor }}
                          ></div>
                          Width: {field.display?.width || 'Not set'}
                        </div>
                        <div className="flex items-center">
                          {field.display?.showInList ? <Eye className="mr-2" size={14} /> : <EyeOff className="mr-2" size={14} />}
                          Show in List: {field.display?.showInList ? 'Yes' : 'No'}
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 dark:bg-gray-600 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center">
                        <CheckCircle className="mr-2" size={16} />
                        Validation Rules
                      </h4>
                      <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                        <div>Required: {field.validation?.required ? 'Yes' : 'No'}</div>
                        {field.validation?.minLength && <div>Min Length: {field.validation.minLength}</div>}
                        {field.validation?.maxLength && <div>Max Length: {field.validation.maxLength}</div>}
                        {field.validation?.min && <div>Min Value: {field.validation.min}</div>}
                        {field.validation?.max && <div>Max Value: {field.validation.max}</div>}
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 dark:bg-gray-600 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center">
                        <LayoutGrid className="mr-2" size={16} />
                        Ticket Types
                      </h4>
                      <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                        {field.ticketTypes && field.ticketTypes.length > 0 ? (
                          field.ticketTypes.map((type, index) => (
                            <div key={index} className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded text-xs">
                              {type}
                            </div>
                          ))
                        ) : (
                          <div>All Types</div>
                        )}
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 dark:bg-gray-600 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center">
                        <AlertCircle className="mr-2" size={16} />
                        Dependencies
                      </h4>
                      <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                        {field.dependencies && field.dependencies.length > 0 ? (
                          field.dependencies.map((dep, index) => (
                            <div key={index} className="bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 px-2 py-1 rounded text-xs">
                              {dep.field} {dep.condition} {dep.value}
                            </div>
                          ))
                        ) : (
                          <div>No Dependencies</div>
                        )}
                      </div>
                    </div>
                  </div>

                  {field.options && field.options.length > 0 && (
                    <div className="mt-4 bg-gray-50 dark:bg-gray-600 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-900 dark:text-white mb-2">Field Options</h4>
                      <div className="flex flex-wrap gap-2">
                        {field.options?.map((option, index) => (
                          <div key={index} className="flex items-center bg-white dark:bg-gray-700 px-3 py-1 rounded border">
                            <div 
                              className="w-3 h-3 rounded-full mr-2"
                              style={{ backgroundColor: option.color }}
                            ></div>
                            <span className="text-sm text-gray-700 dark:text-gray-300">
                              {option.label}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            }) : (
              <div className="text-center py-12">
                <LayoutGrid size={48} className="mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Custom Fields Found</h3>
                <p className="text-gray-600 mb-4">Get started by creating your first custom field.</p>
                <button 
                  onClick={() => setShowCreateForm(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center mx-auto"
                >
                  <Plus size={16} className="inline mr-2" />
                  Create Custom Field
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Form Preview Tab */}
      {activeTab === 'preview' && (
        <div>
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Form Preview
            </h2>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              Preview how your custom fields will appear in the ticket creation form. This shows the actual form that users will see.
            </p>
          </div>

          <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
            <div className="mb-4 pb-4 border-b border-gray-200 dark:border-gray-600">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Ticket Creation Form Preview
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                This is how the form will look to users when creating a ticket
              </p>
            </div>

            {/* Standard Ticket Fields */}
            <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                Standard Fields
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Subject <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Enter ticket subject"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    disabled
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Priority <span className="text-red-500">*</span>
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    disabled
                  >
                    <option>Select Priority</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    placeholder="Describe your issue..."
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    disabled
                  />
                </div>
              </div>
            </div>

            {/* Custom Fields Preview */}
            {customFields && customFields.length > 0 ? (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
                  Custom Fields
                </h4>

                {/* Group fields by position */}
                {['top', 'middle', 'bottom'].map((position) => {
                  const positionFields = customFields.filter(
                    field => (field.display?.position || 'top') === position
                  );

                  if (positionFields.length === 0) return null;

                  return (
                    <div key={position} className="mb-6">
                      {position === 'top' && (
                        <div className="mb-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          Top Section
                        </div>
                      )}
                      {position === 'middle' && (
                        <div className="mb-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          Middle Section
                        </div>
                      )}
                      {position === 'bottom' && (
                        <div className="mb-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          Bottom Section
                        </div>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {positionFields.map((field) => {
                          const widthClass = {
                            quarter: 'md:col-span-1',
                            half: 'md:col-span-2',
                            full: 'md:col-span-4'
                          }[field.display?.width || 'half'] || 'md:col-span-2';

                          return (
                            <div key={field.id} className={widthClass}>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                {field.label || field.name}
                                {field.required && <span className="text-red-500 ml-1">*</span>}
                              </label>
                              {field.description && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                                  {field.description}
                                </p>
                              )}
                              {renderPreviewField(field)}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <LayoutGrid size={32} className="mx-auto text-gray-400 mb-2" />
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  No custom fields added yet. Add fields to see them in the preview.
                </p>
              </div>
            )}

            {/* Preview Form Actions */}
            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-600 flex justify-end space-x-3">
              <button
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-md"
                disabled
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                disabled
              >
                Create Ticket
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Field Testing Tab */}
      {activeTab === 'testing' && (
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
            Field Testing
          </h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Test Field Validation
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Select Field
                  </label>
                  <select
                    value={testData.fieldId}
                    onChange={(e) => setTestData({ ...testData, fieldId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                  >
                    {customFields && customFields.length > 0 ? customFields.map((field) => (
                      <option key={field.id} value={field.id}>
                        {field.label || 'Unnamed'} ({field.type || 'Unknown'})
                      </option>
                    )) : (
                      <option value="">No custom fields available</option>
                    )}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Test Value
                  </label>
                  <input
                    type="text"
                    value={testData.testValue}
                    onChange={(e) => setTestData({ ...testData, testValue: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                    placeholder="Enter test value"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Test Type
                  </label>
                  <select
                    value={testData.testType}
                    onChange={(e) => setTestData({ ...testData, testType: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                  >
                    <option value="validation">Validation</option>
                    <option value="format">Format</option>
                    <option value="dependency">Dependency</option>
                  </select>
                </div>
                
                <button
                  onClick={handleTestField}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center justify-center"
                >
                  <TestTube className="mr-2" size={16} />
                  Test Field
                </button>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Test Results
              </h3>
              
              {testResult ? (
                <div className="space-y-4">
                  <div className={`p-4 rounded-lg ${testResult.result.isValid ? 'bg-green-100 dark:bg-green-900' : 'bg-red-100 dark:bg-red-900'}`}>
                    <div className="flex items-center">
                      {testResult.result.isValid ? (
                        <CheckCircle className="text-green-600 dark:text-green-400 mr-2" size={20} />
                      ) : (
                        <AlertCircle className="text-red-600 dark:text-red-400 mr-2" size={20} />
                      )}
                      <span className={`font-medium ${testResult.result.isValid ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'}`}>
                        {testResult.result.isValid ? 'Valid' : 'Invalid'}
                      </span>
                    </div>
                  </div>
                  
                  {testResult.result.errors && testResult.result.errors.length > 0 && (
                    <div className="bg-red-100 dark:bg-red-900 p-4 rounded-lg">
                      <h4 className="font-medium text-red-800 dark:text-red-200 mb-2">Errors</h4>
                      <ul className="text-sm text-red-700 dark:text-red-300">
                        {testResult.result.errors.map((error, index) => (
                          <li key={index}>• {error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {testResult.result.warnings && testResult.result.warnings.length > 0 && (
                    <div className="bg-yellow-100 dark:bg-yellow-900 p-4 rounded-lg">
                      <h4 className="font-medium text-yellow-800 dark:text-yellow-200 mb-2">Warnings</h4>
                      <ul className="text-sm text-yellow-700 dark:text-yellow-300">
                        {testResult.result.warnings.map((warning, index) => (
                          <li key={index}>• {warning}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {testResult.result.suggestions && testResult.result.suggestions.length > 0 && (
                    <div className="bg-blue-100 dark:bg-blue-900 p-4 rounded-lg">
                      <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">Suggestions</h4>
                      <ul className="text-sm text-blue-700 dark:text-blue-300">
                        {testResult.result.suggestions.map((suggestion, index) => (
                          <li key={index}>• {suggestion}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-gray-500 dark:text-gray-400 text-center py-8">
                  Run a test to see results
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
            Field Analytics
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                  <LayoutGrid className="text-blue-600 dark:text-blue-400" size={24} />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Fields</p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">{customFields?.length || 0}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                  <CheckCircle className="text-green-600 dark:text-green-400" size={24} />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active Fields</p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {customFields?.filter(f => f.status === 'active').length || 0}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
              <div className="flex items-center">
                <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
                  <AlertCircle className="text-orange-600 dark:text-orange-400" size={24} />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Required Fields</p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {customFields?.filter(f => f.required).length || 0}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                  <Eye className="text-purple-600 dark:text-purple-400" size={24} />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Visible in List</p>
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {customFields?.filter(f => f.display?.showInList).length || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Field Types Distribution
              </h3>
              <div className="space-y-4">
                {Array.isArray(fieldTypes) && fieldTypes.map((type) => {
                  const count = customFields?.filter(f => f.type === type.value).length || 0;
                  const percentage = (customFields?.length || 0) > 0 ? (count / (customFields?.length || 1)) * 100 : 0;
                  const IconComponent = getFieldTypeIcon(type.value);
                  const color = getFieldTypeColor(type.value);
                  
                  return (
                    <div key={type.value} className="flex items-center justify-between">
                      <div className="flex items-center">
                        <IconComponent 
                          size={20} 
                          style={{ color }}
                          className="mr-3"
                        />
                        <span className="text-gray-900 dark:text-white">{type.label}</span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-24 bg-gray-200 dark:bg-gray-600 rounded-full h-2 mr-3">
                          <div 
                            className="h-2 rounded-full"
                            style={{ 
                              width: `${percentage}%`,
                              backgroundColor: color 
                            }}
                          ></div>
                        </div>
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {count} ({percentage.toFixed(1)}%)
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Field Usage Trends
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-900 dark:text-white">Most Used Field Type</span>
                  <div className="flex items-center">
                    <Type className="text-blue-600 mr-2" size={16} />
                    <span className="text-blue-600 dark:text-blue-400">Text Input</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-900 dark:text-white">Average Field Length</span>
                  <span className="text-gray-600 dark:text-gray-400">12.5 characters</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-900 dark:text-white">Fields with Dependencies</span>
                  <span className="text-gray-600 dark:text-gray-400">
                    {customFields?.filter(f => f.dependencies?.length > 0).length || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-900 dark:text-white">Searchable Fields</span>
                  <span className="text-gray-600 dark:text-gray-400">
                    {customFields?.filter(f => f.display?.showInSearch).length || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomFieldsManagement;